import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import { Order as OrderComponent } from './Order'
import { Loader } from './Loader'
import { useCore } from '../hooks'
import { printTicket, Schema, useSearchParams } from '../lib'
import { Button, buttonVariants } from './shadcn/Button'
import { cn } from './shadcn/utils'
import { PackageBarcodeUtil } from '../lib/PackageBarcode'
import { GroupRecord } from '../store/core/reducer'
import { useIntersect } from '../lib/hooks/useIntersect'
import { faArrowDown } from '@fortawesome/pro-light-svg-icons'
import { OrdersFilters } from './OrdersFilters'
import { useOrderFilters } from '../lib/hooks/useOrderFilters'
import { useGroups } from '../lib/hooks/useGroups'
import { graphql } from '../gql'
import { useGQLInfinite } from '../hooks/GQLUtilityHooks'
import { useBarcode } from '../hooks/useBarcode'
import { useQueryClient } from '@tanstack/react-query'

const OrdersListQuery = graphql(`
  query OrdersList($page: Int!, $filters: orderFilters, $sort: String, $direction: String) {
    orders(page: $page, filters: $filters, sort: $sort, direction: $direction) {
      items {
        id
        ...OrderListItem_Order
      }
      paginator {
        current
        totalCount
        endPage
      }
    }
  }
`)

export const Orders = () => {
  const { core, isFetching, setIsFetching } = useCore()
  const history = useHistory()
  const {
    filters,
    processStatus,
    resetFilters,
    searchString,
    setSearchString,
    plaintextFilterParams,
    searchType,
    sort,
    direction,
  } = useOrderFilters()
  const { measureRef, isIntersecting } = useIntersect()
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTriedRemoveLastDigitRef = useRef(false) // Track if we've tried removing last digit
  const isAutoRemoveDigitRef = useRef(false) // Mark when we're doing automatic digit removal
  const hasTriedRemoveFirstDigitRef = useRef(false) // Track if we've tried removing first digit (GLS leading 0)
  const isAutoRemoveFirstDigitRef = useRef(false) // Mark when we're doing automatic first digit removal
  const hasTriedRemovePercentPrefixRef = useRef(false) // Track if we've tried removing %00 prefix
  const isAutoRemovePercentRef = useRef(false) // Mark when we're doing automatic %00 removal

  // Build GraphQL-compatible filters
  const gqlFilters = useMemo(() => {
    return {
      ...filters,
      processStatus: filters.processStatus as string | undefined,
      carriers: filters.carriers as string[] | undefined,
      checkouts: filters.checkouts as number[] | undefined,
      shippingCountries: filters.shippingCountries as string[] | undefined,
      companies: filters.companies as number[] | undefined,
      group: filters.group as number | undefined,
      tags: filters.tags as number[] | undefined,
      processStatusChange: (filters as any).processStatusChange as string | undefined,
      processStatusUpdated: (filters as any).processStatusUpdated as
        | { left_date?: string; right_date?: string }
        | undefined,
      fulltext:
        ('fulltext' in plaintextFilterParams ? plaintextFilterParams.fulltext : undefined) ||
        (searchType === 'fulltext' && searchString) ||
        undefined,
      customerFulltext: ('customerFulltext' in plaintextFilterParams
        ? plaintextFilterParams.customerFulltext
        : undefined) as string | undefined,
      box: ('box' in plaintextFilterParams ? plaintextFilterParams.box : undefined) as string | undefined,
      reservationNumber: ('reservationNumber' in plaintextFilterParams
        ? plaintextFilterParams.reservationNumber
        : undefined) as number | undefined,
      billNumber: ('billNumber' in plaintextFilterParams ? plaintextFilterParams.billNumber : undefined) as
        | number
        | undefined,
      package: ('package' in plaintextFilterParams ? plaintextFilterParams.package : undefined) as string | undefined,
    }
  }, [filters, plaintextFilterParams, searchString])

  const {
    data,
    isLoading,
    isFetching: isQueryFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGQLInfinite(OrdersListQuery, ['orders'], {
    page: 1,
    filters: gqlFilters,
    sort,
    direction,
  })

  const queryClient = useQueryClient()

  // Limit caching to 1 page, to avoid memory usage and huge re-renders
  useEffect(() => {
    console.log('cache updating')
    queryClient.setQueriesData(
      { queryKey: ['orders'], type: 'inactive' },
      (data: { pages: any[]; pageParams: any[] }) => {
        console.log(data)
        return {
          pages: (data?.pages || []).slice(0, 1),
          pageParams: (data?.pageParams || []).slice(0, 1),
        }
      },
    )
  }, [isQueryFetching])

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0)
    }
  }, [filters.processStatus])

  // Reset tracking when search string changes (only for manual changes)
  useEffect(() => {
    if (!isAutoRemoveDigitRef.current && !isAutoRemoveFirstDigitRef.current) {
      hasTriedRemoveLastDigitRef.current = false
      hasTriedRemoveFirstDigitRef.current = false
    }
    if (!isAutoRemovePercentRef.current) {
      hasTriedRemovePercentPrefixRef.current = false
    }
    isAutoRemoveDigitRef.current = false
    isAutoRemoveFirstDigitRef.current = false
    isAutoRemovePercentRef.current = false
  }, [searchString])

  // Search replacement logic for ZASILKOVNA, GLS and DPD DE (only when search type is 'package')
  useEffect(() => {
    if (!isLoading && data.length === 0 && searchString && searchString.length > 1 && searchType === 'package') {
      // Case 1: Remove Z prefix if Z is followed by a number - ZASILKOVNA
      if (searchString.startsWith('Z') && /\d/.test(searchString[1])) {
        const searchWithoutZ = searchString.substring(1)
        setSearchString(searchWithoutZ)
      }
      // Case 2a: Remove last digit if search is only numbers (only once per search) - GLS
      // Skip if we just did Case 3 (DPD), as the result would be only numbers
      else if (
        /^\d+$/.test(searchString) &&
        !hasTriedRemoveLastDigitRef.current &&
        !hasTriedRemovePercentPrefixRef.current
      ) {
        hasTriedRemoveLastDigitRef.current = true
        isAutoRemoveDigitRef.current = true
        const searchWithoutLastDigit = searchString.slice(0, -1)
        setSearchString(searchWithoutLastDigit)
      }
      // Case 2b: Remove leading 0 after last digit removal didn't help - GLS with leading zero (e.g. 050312696001 → 5031269600)
      else if (
        /^0\d+$/.test(searchString) &&
        hasTriedRemoveLastDigitRef.current &&
        !hasTriedRemoveFirstDigitRef.current
      ) {
        hasTriedRemoveFirstDigitRef.current = true
        isAutoRemoveFirstDigitRef.current = true
        setSearchString(searchString.substring(1))
      }
      // Case 3: Remove % and first 7 digits and last 6 digits - DPD DE
      else if (searchString.startsWith('%') && !hasTriedRemovePercentPrefixRef.current) {
        hasTriedRemovePercentPrefixRef.current = true
        isAutoRemovePercentRef.current = true // Mark that we're doing automatic removal
        // Remove % and first 7 digits and last 6 digits
        const withoutPrefix = searchString.substring(8) // Remove "%00"
        const withoutLastSix = withoutPrefix.slice(0, -6) // Remove last 6 digits
        setSearchString(withoutLastSix)
      }
    }
  }, [isLoading, data, searchString, searchType])

  // Open order, when after filtering only one order is left
  useEffect(() => {
    if (!isLoading && data.length === 1 && !!searchString && searchString.length > 0) {
      setSearchString('')
      setTimeout(() => {
        // Avoid race condition with the redirect to right query params url after the search string is cleared
        history.push(`/order/${data[0].id}`)
      }, 0)
    }
  }, [isLoading, data, searchString, history])

  useEffect(() => {
    if (isIntersecting && hasNextPage) {
      fetchNextPage()
    }
  }, [isIntersecting, hasNextPage, fetchNextPage])

  useEffect(() => {
    // Only redirect if we're on the base dashboard route with no filters at all
    const urlParams = new URLSearchParams(window.location.search)
    const hasUrlFilters = urlParams.toString().length > 0

    if ((!processStatus || processStatus == 'all') && !searchString && !hasUrlFilters) {
      // Redirect to packing dashboard if no filters are set
      history.push('/dashboard/packing')
    }
  }, [processStatus, searchString, history])

  // Handle barcode scanning for order search
  useBarcode(
    event => {
      if (!location.href?.includes('/order/')) {
        setSearchString(PackageBarcodeUtil.getPackageCodeFromBarcode(event.data))
      }
    },
    { autoStart: true },
  )

  return isFetching || (isLoading && data.length <= 0) ? (
    <Loader size="fill-container" />
  ) : (
    <div className={'p-4 grid gap-2 orderColumns overflow-y-auto'} ref={containerRef}>
      {data.length > 0 && data.map(o => <OrderComponent order={o} key={o.id} />)}
      {hasNextPage && (
        <Button
          ref={measureRef}
          id={'next-orders'}
          variant={isFetchingNextPage ? 'disabled' : 'default'}
          title={'Načíst další'}
          disabled={isLoading}
          onClick={() => fetchNextPage()}>
          {isFetchingNextPage ? <div className="loader-small"></div> : 'Načíst další'}
        </Button>
      )}
    </div>
  )
}
